import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Alert,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Close } from '@mui/icons-material';
import { en } from '../translations/en';

interface MFAVerificationProps {
  open: boolean;
  onVerify: (code: string) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  error?: string | null;
  mfaMethod?: 'totp' | 'sms';
}

export const MFAVerification: React.FC<MFAVerificationProps> = ({
  open,
  onVerify,
  onCancel,
  loading = false,
  error,
  mfaMethod,
}) => {
  const theme = useTheme();
  const fullScreenMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [code, setCode] = useState('');

  const handleSubmit = async () => {
    if (code.length === 6) {
      await onVerify(code);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && code.length === 6) {
      handleSubmit();
    }
  };

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth fullScreen={fullScreenMobile}>
      <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        {en.mfaVerification.title}
        <IconButton
          aria-label="close"
          onClick={onCancel}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          {mfaMethod === 'sms' ? en.mfaVerification.smsDescription : en.mfaVerification.description}
        </Typography>

        <TextField
          fullWidth
          label={en.mfaVerification.authCode}
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          onKeyPress={handleKeyPress}
          placeholder="123456"
          inputProps={{ maxLength: 6 }}
          sx={{ mt: 2 }}
          autoFocus
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: `1px solid ${theme.palette.divider}`, p: 2 }}>
        <Button onClick={onCancel}>{en.mfaVerification.cancel}</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={code.length !== 6 || loading}
        >
          {loading ? en.mfaVerification.verifying : en.mfaVerification.verify}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
